import { createEffect, restore, sample } from 'effector';
import { t } from 'i18n';
import _ from 'lodash';
import $$api from 'models/api';
import $$auth from 'models/auth';
import $$ep from 'models/ep';
import $$i18n from 'models/i18n';
import { $isPrePaymentPage, $isSummaryPage } from 'models/location';
import { showModal } from 'models/modals';
import $$prePaymentPage from 'models/pages/pre-payment';
import { navigateFx } from 'models/router-gate';
import { $global } from 'models/store-global';

import DashboardService from 'services/DashboardService';
import EstatePlanService from 'services/EstatePlanService';
import { opposite } from 'utils/common-helper';

import { Plan } from 'types/zod/plan';
import { type PlanCode } from 'types/zod/plan-code';
import { type ProductChoice } from 'types/zod/product-choice';

const $isOneOfSummaryPages = sample({
    source: {
        isPrePaymentPage: $isPrePaymentPage,
        isSummaryPage: $isSummaryPage,
    },
    fn: ({ isPrePaymentPage, isSummaryPage }) => isPrePaymentPage || isSummaryPage,
});

// When pre-payment page is opened, load participants if was not loaded before
sample({
    source: restore($$api.participants.getParticipantsFx.doneData, null),
    clock: $isOneOfSummaryPages,
    filter: (participants, isOneOfSummaryPages) => participants === null && isOneOfSummaryPages,
    fn: () => undefined,
    target: $$api.participants.getParticipantsFx,
});

// If getParticipants responded with at least one participant that sutisfies EstatePlanService.getIsCharityLinked, then set true to $charityLinked
sample({
    source: $$api.participants.getParticipantsFx.doneData,
    fn: EstatePlanService.getIsCharityLinked,
    target: $$prePaymentPage.$charityLinked,
});

// If getParticipants responded with at least one participant that sutisfies EstatePlanService.getIsCharitySelected, then set true to $charityLinked
sample({
    source: $$ep.stores.$epAnswers,
    fn: EstatePlanService.getIsCharitySelected,
    target: $$prePaymentPage.$charitySelected,
});

// If was_generated_with_charity_advisor is exists, then set true to $charityLinked
sample({
    source: $$ep.stores.$epAnswers,
    fn: EstatePlanService.getWasGeneratedWithCharity,
    target: $$prePaymentPage.$wasGeneratedWithCharity,
});

const revertPlanFx = createEffect(DashboardService.cancelEstatePlan);

// When modality close button is clicked, revert EP to original and navigate to dashboard
sample({
    source: $$ep.stores.$epQInstanceId,
    clock: $$prePaymentPage.modalityCloseClick,
    filter: Boolean,
    target: revertPlanFx,
});
sample({
    clock: revertPlanFx.doneData,
    fn: () => '/dashboard',
    target: navigateFx,
});

// When modality confirm button is clicked, go to dashboard
sample({
    clock: $$prePaymentPage.modalityConfirmClick,
    fn: () => '/dashboard',
    target: navigateFx,
});

const getPlanPrice = (plans: Plan[], plan_code: PlanCode | undefined) =>
    _.find(plans, { plan_code })?.one_time_fee ?? 0;

const getProductPrice = (plans: Plan[], product_choice: ProductChoice, couple: boolean) =>
    _.find(plans, { product_choice, couple })?.one_time_fee ?? 0;

const $isUpgrading = sample({
    source: {
        plans: $global.map((state) => state.temp?.plans || []),
        selectedProduct: $$ep.$selectedProduct.map((product) => product || 'simple_product_choice'),
        isCouple: $$ep.$isSingle.map(Boolean).map(opposite),
        purchasedPlanCode: $$auth.$currentParticipant.map((p) => p?.user?.plan?.plan_code),
    },
    fn: ({ plans, selectedProduct, isCouple, purchasedPlanCode }) =>
        getProductPrice(plans, selectedProduct, isCouple) > getPlanPrice(plans, purchasedPlanCode),
});

sample({
    source: {
        _isPrePaymentPage: $isPrePaymentPage,
        _resourcesLoaded: restore($$i18n.localesLoaded.map(Boolean).map(opposite), false),
        charityLinked: $$prePaymentPage.$charityLinked,
        charitySelected: $$prePaymentPage.$charitySelected,
        wasGeneratedWithCharity: $$prePaymentPage.$wasGeneratedWithCharity,
        isAlreadyPaid: $$auth.$currentParticipant.map((p) => p?.user?.plan?.plan_code).map(Boolean),
        isUpgrading: $isUpgrading,
        selectedProduct: $$ep.$selectedProduct,
    },
    fn: ({
        charityLinked,
        charitySelected,
        wasGeneratedWithCharity,
        isAlreadyPaid,
        isUpgrading,
        selectedProduct,
    }): string => {
        const youCompletedTextOptions: { is: boolean; resources: string }[] = [
            {
                is: !charityLinked && isAlreadyPaid && isUpgrading,
                resources: 'youCompletedText.paid.upgrade',
            },
            {
                is: !charityLinked && isAlreadyPaid && !isUpgrading,
                resources: 'youCompletedText.paid.notupgrade',
            },
            {
                is: !charityLinked && !isAlreadyPaid,
                resources: `youCompletedText.unpaid.${selectedProduct || 'simple_product_choice'}`,
            },
            {
                is: charityLinked && charitySelected && !wasGeneratedWithCharity,
                resources: 'youCompletedText.charitySelected.wasNotGeneratedWithCharity',
            },
            {
                is: charityLinked && !charitySelected && !wasGeneratedWithCharity,
                resources: 'youCompletedText.charityNotSelected.wasNotGeneratedWithCharity',
            },
            {
                is: charityLinked && !charitySelected && wasGeneratedWithCharity,
                resources: 'youCompletedText.charityNotSelected.wasGeneratedWithCharity',
            },
            {
                is: charityLinked && charitySelected && wasGeneratedWithCharity,
                resources: 'youCompletedText.charitySelected.wasGeneratedWithCharity',
            },
        ];
        return youCompletedTextOptions.find((option) => option.is)?.resources || '';
    },
    target: $$prePaymentPage.$youCompletedTextResource,
});

sample({
    source: {
        charityLinked: $$prePaymentPage.$charityLinked,
        charitySelected: $$prePaymentPage.$charitySelected,
        wasGeneratedWithCharity: $$prePaymentPage.$wasGeneratedWithCharity,
    },
    fn: ({ charityLinked, charitySelected, wasGeneratedWithCharity }): boolean =>
        charityLinked && !charitySelected && wasGeneratedWithCharity,
    target: $$prePaymentPage.$mustGoToModality,
});

sample({
    source: { isPrePaymentPage: $isPrePaymentPage, isSummaryPage: $isSummaryPage },
    clock: $$prePaymentPage.openMissingCharityModality,
    fn: ({ isPrePaymentPage, isSummaryPage }) => {
        const pageNameInResources = isPrePaymentPage ? 'prePayment' : isSummaryPage ? 'summary' : '';
        return {
            modalName: 'MODALITY' as const,
            state: {
                title: t(`${pageNameInResources}.wasNotGeneratedWithCharityModality.title`),
                body: t(`${pageNameInResources}.wasNotGeneratedWithCharityModality.body`),
                options: {
                    closeButton: t(`${pageNameInResources}.wasNotGeneratedWithCharityModality.closeButton`),
                    confirmButton: t(`${pageNameInResources}.wasNotGeneratedWithCharityModality.confirmButton`),
                    onCancel: $$prePaymentPage.modalityCloseClick,
                    onConfirm: $$prePaymentPage.modalityConfirmClick,
                },
            },
        };
    },
    target: showModal,
});
